/**
 * One inline video player with an Original / Optimized switch, for a quick
 * A/B look at the result. Side-by-side players were considered and skipped:
 * two small videos drifting out of sync are harder to compare than one video
 * that swaps source in place at the same timestamp.
 *
 * Like the model preview, the player is mounted ONCE into a stable slot that
 * renderApp never rewrites — the page re-renders on every progress tick, and
 * rebuilding a <video> each time would restart playback.
 */
type Source = 'original' | 'optimized';

export class VideoPlayer {
  private slot: HTMLElement | null = null;
  private video: HTMLVideoElement | null = null;
  private toggle: HTMLElement | null = null;
  private message: HTMLElement | null = null;
  private urls: Record<Source, string | null> = { original: null, optimized: null };
  private current: Source = 'original';
  /** The one outstanding restore-after-load handler (see `select`). */
  private pendingRestore: (() => void) | null = null;

  private mount(slot: HTMLElement): void {
    if (this.slot) return;
    this.slot = slot;
    slot.innerHTML = `
      <div class="segmented player-toggle" role="radiogroup" aria-label="Which video to play" hidden>
        <label><input type="radio" name="player-source" value="original" /><span>Original</span></label>
        <label><input type="radio" name="player-source" value="optimized" /><span>Optimized</span></label>
      </div>
      <video class="player-video" controls muted playsinline loop preload="metadata"></video>
      <p class="player-message" hidden></p>
    `;
    this.video = slot.querySelector('video');
    this.toggle = slot.querySelector('.player-toggle');
    this.message = slot.querySelector('.player-message');

    this.toggle?.addEventListener('change', (e) => {
      const value = (e.target as HTMLInputElement).value;
      if (value === 'original' || value === 'optimized') this.select(value);
    });
    // Browsers can't play every file we can analyze (e.g. HEVC in Chrome, MKV
    // in Safari). Say so in place of a broken player.
    this.video?.addEventListener('error', () => {
      this.setMessage(this.current === 'original'
        ? 'This browser can’t play the original file. The analysis above still applies.'
        : 'This browser can’t play the optimized file.');
    });
    this.video?.addEventListener('loadeddata', () => {
      if (!this.video?.error) this.setMessage('');
    });
  }

  show(slot: HTMLElement): void {
    this.mount(slot);
    slot.hidden = false;
  }

  hide(): void {
    this.video?.pause();
    if (this.slot) this.slot.hidden = true;
  }

  /** A new source file: play it from the start, and forget any previous result. */
  setOriginal(file: File): void {
    this.replaceUrl('original', file);
    this.replaceUrl('optimized', null);
    this.select('original', false);
  }

  /** A fresh optimized result: switch to it so the user sees what they'll download. */
  setOptimized(blob: Blob): void {
    this.replaceUrl('optimized', blob);
    this.select('optimized', true);
  }

  private replaceUrl(which: Source, blob: Blob | null): void {
    const old = this.urls[which];
    if (old) URL.revokeObjectURL(old);
    this.urls[which] = blob ? URL.createObjectURL(blob) : null;
    if (this.toggle) this.toggle.hidden = !this.urls.optimized;
  }

  /**
   * Swap sources. `keepPosition` carries the playhead and play/pause state
   * across — right for the Original/Optimized comparison, wrong for a brand-new
   * file, which starts from 0.
   */
  private select(which: Source, keepPosition = true): void {
    const video = this.video;
    const url = this.urls[which];
    if (!video || !url) return;
    const time = keepPosition ? video.currentTime : 0;
    const wasPlaying = keepPosition && !video.paused && !video.ended;
    this.current = which;
    this.toggle?.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
      input.checked = input.value === which;
    });
    if (video.src === url) return;
    this.setMessage('');
    // Only the latest swap may restore position: a quick Original → Optimized →
    // Original would otherwise leave an earlier handler seeking to a stale time.
    if (this.pendingRestore) video.removeEventListener('loadedmetadata', this.pendingRestore);
    const restore = (): void => {
      this.pendingRestore = null;
      if (time > 0 && time < video.duration) video.currentTime = time;
      if (wasPlaying) void video.play().catch(() => { /* autoplay refused — controls remain */ });
    };
    this.pendingRestore = restore;
    video.addEventListener('loadedmetadata', restore, { once: true });
    video.src = url;
  }

  private setMessage(text: string): void {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.hidden = !text;
  }
}

/** Singleton — there is only ever one player on the page. */
export const videoPlayer = new VideoPlayer();
